import { useState, useEffect, useMemo } from 'react';
import { api } from '../lib/api';
import { getCachedCatalog, setCachedCatalog } from '../lib/catalogCache';
import { useBasket } from '../hooks/useBasket';
import ProductImageModal from '../components/ProductImageModal';
import BasketIcon from '../components/BasketIcon';
import Recommendations from '../components/Recommendations';

const PAR_STORAGE_KEY = 'ss_par_levels';
const CUSTOM_GUIDE_KEY = 'ss_custom_guide_items';
const HIDDEN_GUIDE_KEY = 'ss_hidden_guide_items';

function loadParLevels() {
  try { return JSON.parse(localStorage.getItem(PAR_STORAGE_KEY)) || {}; } catch { return {}; }
}
function saveParLevels(levels) {
  localStorage.setItem(PAR_STORAGE_KEY, JSON.stringify(levels));
}
function loadCustomGuideItems() {
  try { return JSON.parse(localStorage.getItem(CUSTOM_GUIDE_KEY)) || []; } catch { return []; }
}
function saveCustomGuideItems(items) {
  localStorage.setItem(CUSTOM_GUIDE_KEY, JSON.stringify(items));
}
function loadHiddenItems() {
  try { return JSON.parse(localStorage.getItem(HIDDEN_GUIDE_KEY)) || []; } catch { return []; }
}
function saveHiddenItems(items) {
  localStorage.setItem(HIDDEN_GUIDE_KEY, JSON.stringify(items));
}

export default function NewOrderGuide() {
  const [guideItems, setGuideItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantities, setQuantities] = useState({});
  const [parLevels, setParLevels] = useState(loadParLevels);
  const [search, setSearch] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [customItems, setCustomItems] = useState(loadCustomGuideItems);
  const [hiddenItems, setHiddenItems] = useState(loadHiddenItems);
  const [zoomProduct, setZoomProduct] = useState(null);

  const { addItems } = useBasket();

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      try {
        // Load catalog (cached or fresh)
        let catalogData = getCachedCatalog();
        if (!catalogData) {
          catalogData = await api.getCatalog();
          setCachedCatalog(catalogData);
        }

        // Load order guide
        const guideData = await api.getOrderGuide();

        if (cancelled) return;

        const products = catalogData.products || [];
        setCatalog(products);

        // Build a lookup map from catalog
        const bySosId = {};
        const bySku = {};
        products.forEach(p => {
          if (p.sosId) bySosId[String(p.sosId)] = p;
          if (p.sku) bySku[String(p.sku)] = p;
        });

        // Merge guide items with catalog details
        const guide = (guideData.guide || []).map(g => {
          const catalogProduct =
            (g.itemId && bySosId[String(g.itemId)]) ||
            (g.itemNumber && bySku[String(g.itemNumber)]) ||
            null;
          return {
            ...g,
            product: catalogProduct,
            id: catalogProduct?.id || g.itemId || g.itemNumber,
            name: catalogProduct?.name || `Item ${g.itemNumber || g.itemId}`,
            sku: catalogProduct?.sku || g.itemNumber || '',
            brand: catalogProduct?.brand || '',
            description: catalogProduct?.description || '',
            hasImage: !!catalogProduct?.hasImage,
            price: catalogProduct?.price || 0,
            onSale: !!catalogProduct?.onSale,
            salePrice: catalogProduct?.salePrice ?? null,
            originalPrice: catalogProduct?.originalPrice ?? catalogProduct?.price ?? 0,
            saleLabel: catalogProduct?.saleLabel || null,
            sosId: catalogProduct?.sosId || g.itemId,
          };
        });

        setGuideItems(guide);
        setQuantities({});
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, []);

  const setQty = (id, val) => {
    const n = Math.max(0, parseInt(val) || 0);
    setQuantities(q => ({ ...q, [id]: n }));
  };

  const setParLevel = (id, val) => {
    const n = Math.max(0, parseInt(val) || 0);
    setParLevels(prev => {
      const updated = { ...prev, [id]: n };
      if (n === 0) delete updated[id];
      saveParLevels(updated);
      return updated;
    });
  };

  function removeFromGuide(item) {
    if (item.isCustom) {
      // Remove custom item from localStorage
      const updated = customItems.filter(c => String(c.id) !== String(item.id));
      setCustomItems(updated);
      saveCustomGuideItems(updated);
    } else {
      // Hide server/preloaded item
      const updated = [...hiddenItems, String(item.id)];
      setHiddenItems(updated);
      saveHiddenItems(updated);
    }
  }

  // Merge server guide items with custom (locally added) items, excluding hidden
  const hiddenSet = useMemo(() => new Set(hiddenItems.map(String)), [hiddenItems]);

  const allGuideItems = useMemo(() => {
    const visibleServer = guideItems.filter(g => !hiddenSet.has(String(g.id)));
    const serverIds = new Set(visibleServer.map(g => String(g.id)));
    const extras = customItems.filter(c => !serverIds.has(String(c.id)) && !hiddenSet.has(String(c.id))).map(c => ({
      ...c,
      itemId: c.sosId || c.id,
      orderCount: 0,
      lastQty: 0,
      lastOrdered: null,
      product: c,
      isCustom: true,
    }));
    return [...visibleServer, ...extras];
  }, [guideItems, customItems, hiddenSet]);

  // Filter by search
  const filteredItems = useMemo(() => {
    if (!search) return allGuideItems;
    const q = search.toLowerCase();
    return allGuideItems.filter(item =>
      item.name?.toLowerCase().includes(q) ||
      item.sku?.toLowerCase().includes(q) ||
      item.brand?.toLowerCase().includes(q) ||
      item.description?.toLowerCase().includes(q)
    );
  }, [allGuideItems, search]);

  // Catalog modal: filter catalog items not already in guide
  const guideIdSet = useMemo(() => {
    const ids = new Set();
    allGuideItems.forEach(g => {
      ids.add(String(g.id));
      if (g.sosId) ids.add(String(g.sosId));
      if (g.sku) ids.add(String(g.sku));
    });
    return ids;
  }, [allGuideItems]);

  const filteredCatalog = useMemo(() => {
    let items = catalog.filter(p => {
      return !guideIdSet.has(String(p.id)) && !guideIdSet.has(String(p.sosId));
    });
    if (catalogSearch) {
      const q = catalogSearch.toLowerCase();
      items = items.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.sku?.toLowerCase().includes(q) ||
        p.brand?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q)
      );
    }
    return items;
  }, [catalog, guideIdSet, catalogSearch]);

  function addToGuide(product) {
    const newItem = {
      id: product.id,
      sosId: product.sosId,
      name: product.name,
      sku: product.sku,
      brand: product.brand,
      description: product.description || '',
      hasImage: !!product.hasImage,
      price: product.price,
      onSale: !!product.onSale,
      originalPrice: product.originalPrice ?? product.price,
      saleLabel: product.saleLabel || null,
    };
    const updated = [...customItems, newItem];
    setCustomItems(updated);
    saveCustomGuideItems(updated);
  }

  function handleAddToBasket() {
    const itemsToAdd = allGuideItems
      .filter(g => (quantities[g.id] || 0) > 0)
      .map(g => ({
        itemId: g.sosId || g.id,
        sosId: g.sosId,
        name: g.name,
        description: g.description || '',
        sku: g.sku,
        quantity: quantities[g.id],
        price: g.price,
        onSale: !!g.onSale,
        originalPrice: g.originalPrice ?? g.price,
        saleLabel: g.saleLabel || null,
      }));

    if (itemsToAdd.length === 0) return;

    addItems(itemsToAdd);
    setQuantities({});
    setSuccessMsg('Items added to basket');
    setTimeout(() => setSuccessMsg(''), 4000);
  }

  const orderLines = allGuideItems.filter(g => (quantities[g.id] || 0) > 0);
  const orderTotal = orderLines.reduce((sum, g) => sum + (g.price || 0) * (quantities[g.id] || 0), 0);

  if (loading) return <div style={styles.loading}>Loading order guide...</div>;

  return (
    <div style={styles.root}>
      {/* Header */}
      <div data-page-header style={styles.pageHeader}>
        <div>
          <h1 style={styles.title}>Order Guide</h1>
          <p style={styles.subtitle}>Your frequently ordered items with PAR levels</p>
        </div>
        <button style={styles.addFromCatalogBtn} onClick={() => setShowCatalogModal(true)}>
          + Add from Catalog
        </button>
      </div>

      {/* Specials banner — only when this customer has active specials */}
      {catalog.some(p => p.onSale) && (
        <a href="#specials" style={styles.specialsBanner}>
          🔥 {catalog.filter(p => p.onSale).length} product{catalog.filter(p => p.onSale).length === 1 ? '' : 's'} on special right now — view Specials →
        </a>
      )}

      {/* Success / Error banners */}
      {successMsg && (
        <div style={styles.successBanner}>
          {successMsg}
          <a href="#basket" style={styles.viewBasketLink}>View Basket</a>
          <button style={styles.dismissBtn} onClick={() => setSuccessMsg('')}>x</button>
        </div>
      )}
      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* True-empty state: this customer has no order history yet AND no
          custom items. Show a friendly CTA pointing at the catalog. We
          differentiate this from "search returned nothing" below — the
          message and action should be different. */}
      {!loading && allGuideItems.length === 0 && (
        <div style={styles.emptyGuide}>
          <div style={styles.emptyGuideIcon}>📋</div>
          <h2 style={styles.emptyGuideTitle}>Your Order Guide is empty</h2>
          <p style={styles.emptyGuideText}>
            Once you've placed a few orders, your regular items will show up here for quick re-ordering.
            For now, browse the full catalog to start adding products.
          </p>
          <a href="#catalog" style={styles.emptyGuideBtn}>Browse Catalog →</a>
          <button style={styles.emptyGuideSecondaryBtn} onClick={() => setShowCatalogModal(true)}>
            Or pick items to add to your guide
          </button>
        </div>
      )}

      {/* Upsell strip. Renders nothing until the backend has picks, so it
          costs an empty guide nothing — and when the guide IS empty it's the
          one thing on the page that isn't a dead end. */}
      <Recommendations
        title="Recommended for you"
        subtitle="Based on what businesses like yours order"
        limit={6}
        onAdded={items => {
          addItems(items);
          setSuccessMsg('Added to basket');
          setTimeout(() => setSuccessMsg(''), 4000);
        }}
      />

      {/* Search + table — only shown when the guide actually has items */}
      {allGuideItems.length > 0 && (
      <>
      <div style={styles.searchRow}>
        <input
          style={styles.search}
          placeholder="Search by name, SKU, brand, or product code..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span style={styles.resultCount}>{filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <div data-product-table style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={th()}>Product</th>
              <th style={th('center')}>PAR Level</th>
              <th style={th('center')}>Last Ordered</th>
              <th style={th('center')}>Last Qty</th>
              <th style={th('right')}>Price</th>
              <th style={th('center')}>Order Qty</th>
              <th style={th('right')}>Line Total</th>
              <th style={th('center')}></th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 && (
              <tr><td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: 'rgba(0,0,0,0.3)' }}>No items match your search.</td></tr>
            )}
            {filteredItems.map((item, idx) => {
              const qty = quantities[item.id] || 0;
              const lineTotal = (item.price || 0) * qty;
              const par = parLevels[item.id] || 0;
              const parMismatch = par > 0 && qty !== par;

              return (
                <tr key={`${item.id}-${idx}`} data-product-row style={styles.row}>
                  {/* Product */}
                  <td data-cell="product" style={styles.tdProduct}>
                    <div style={styles.productRow}>
                      {item.hasImage && item.sku && (
                        <img
                          data-product-thumb
                          src={`/images/products/${item.sku}.jpg`}
                          alt=""
                          loading="lazy"
                          style={styles.thumbnail}
                          onClick={() => setZoomProduct(item)}
                          onError={e => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                      <div style={styles.productText}>
                        <div style={styles.productName}>{item.name}</div>
                        <div style={styles.productMeta}>
                          {item.sku && <span style={styles.sku}>{item.sku}</span>}
                          {item.brand && <span style={styles.brand}>{item.brand}</span>}
                          {item.orderCount > 0 && <span style={styles.orderBadge}>Ordered {item.orderCount}x</span>}
                          {item.isCustom && <span style={styles.customBadge}>Custom</span>}
                          {item.onSale && <span style={styles.saleBadge}>{item.saleLabel || 'SALE'}</span>}
                        </div>
                        {item.description && (
                          <div style={styles.description}>{item.description}</div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* PAR Level */}
                  <td data-cell="par" style={td('center')}>
                    <input
                      style={{
                        ...styles.parInput,
                        borderColor: par > 0 ? '#A50B18' : '#ddd',
                        color: par > 0 ? '#A50B18' : '#999',
                      }}
                      type="number"
                          inputMode="numeric"
                      min="0"
                      value={par || ''}
                      placeholder="--"
                      onChange={e => setParLevel(item.id, e.target.value)}
                    />
                  </td>

                  {/* Last Ordered */}
                  <td data-cell="last-ordered" style={{ ...td('center'), fontSize: '0.8rem', color: 'rgba(0,0,0,0.4)' }}>
                    {item.lastOrdered
                      ? new Date(item.lastOrdered).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : '--'}
                  </td>

                  {/* Last Qty */}
                  <td data-cell="last-qty" style={{ ...td('center'), fontFamily: 'monospace', fontSize: '0.85rem', color: 'rgba(0,0,0,0.5)' }}>
                    {item.lastQty || '--'}
                  </td>

                  {/* Price */}
                  <td data-cell="price" style={{ ...td('right'), fontFamily: 'monospace', color: '#A50B18' }}>
                    {item.onSale && item.originalPrice > item.price ? (
                      <>
                        <span style={{ textDecoration: 'line-through', color: 'rgba(0,0,0,0.35)', fontSize: '0.8em' }}>
                          ${item.originalPrice.toFixed(2)}
                        </span>
                        <div>${item.price.toFixed(2)}</div>
                      </>
                    ) : (
                      item.price ? `$${item.price.toFixed(2)}` : '--'
                    )}
                  </td>

                  {/* Order Qty */}
                  <td data-cell="qty" style={td('center')}>
                    <div style={styles.qtyControl}>
                      <button style={styles.qtyBtn} onClick={() => setQty(item.id, qty - 1)}>-</button>
                      <input
                        style={{
                          ...styles.qtyInput,
                          ...(parMismatch ? styles.qtyInputParMismatch : {}),
                        }}
                        type="number"
                          inputMode="numeric"
                        min="0"
                        value={qty || ''}
                        placeholder="0"
                        onChange={e => setQty(item.id, e.target.value)}
                      />
                      <button style={styles.qtyBtn} onClick={() => setQty(item.id, qty + 1)}>+</button>
                    </div>
                    {parMismatch && (
                      <div style={styles.parHint}>PAR: {par}</div>
                    )}
                  </td>

                  {/* Line Total */}
                  <td data-cell="line-total" style={{ ...td('right'), fontFamily: 'monospace', color: qty > 0 ? '#333' : 'rgba(0,0,0,0.2)' }}>
                    {qty > 0 ? `$${lineTotal.toFixed(2)}` : '--'}
                  </td>

                  {/* Remove */}
                  <td data-cell="remove" style={td('center')}>
                    <button
                      data-remove-btn
                      style={{ background: 'none', border: 'none', color: '#ccc', cursor: 'pointer', fontSize: '1.1rem', padding: '0.2rem 0.4rem', lineHeight: 1 }}
                      onClick={() => removeFromGuide(item)}
                      title="Remove from order guide"
                      onMouseEnter={e => e.target.style.color = '#e74c3c'}
                      onMouseLeave={e => e.target.style.color = '#ccc'}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </>
      )}

      {/* Add to Basket panel — on mobile this becomes a fixed footer
          (see [data-pending-basket-bar] in index.html) so customers don't
          have to scroll back to find it after picking items at the top. */}
      {orderLines.length > 0 && (
        <div data-pending-basket-bar style={styles.basketPanel}>
          <div style={styles.basketSummary}>
            <span style={styles.basketSummaryText}>
              <BasketIcon />
              {orderLines.length} item{orderLines.length !== 1 ? 's' : ''} ready &middot; <strong style={styles.basketTotal}>${orderTotal.toFixed(2)}</strong>
            </span>
            <div style={styles.basketActions}>
              <button data-pending-clear style={styles.clearBtn} onClick={() => setQuantities({})}>Clear All</button>
              <button data-add-basket style={styles.addToBasketBtn} onClick={handleAddToBasket}>
                <BasketIcon light />
                Add to Basket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add from Catalog Modal */}
      {showCatalogModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCatalogModal(false)}>
          <div data-modal-content style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Add from Catalog</h2>
              <button style={styles.modalCloseBtn} onClick={() => setShowCatalogModal(false)}>x</button>
            </div>
            <input
              style={styles.modalSearch}
              placeholder="Search catalog..."
              value={catalogSearch}
              onChange={e => setCatalogSearch(e.target.value)}
              autoFocus
            />
            <div style={styles.modalList}>
              {filteredCatalog.length === 0 && (
                <div style={styles.modalEmpty}>No products found</div>
              )}
              {filteredCatalog.slice(0, 50).map(p => (
                <div key={p.id} style={styles.modalItem}>
                  {p.hasImage && p.sku && (
                    <img
                      src={`/images/products/${p.sku}.jpg`}
                      alt=""
                      loading="lazy"
                      style={styles.thumbnail}
                      onClick={() => setZoomProduct(p)}
                      onError={e => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  <div style={styles.modalItemInfo}>
                    <div style={styles.modalItemName}>{p.name}</div>
                    <div style={styles.modalItemMeta}>
                      {p.sku && <span style={styles.sku}>{p.sku}</span>}
                      {p.brand && <span style={styles.brand}>{p.brand}</span>}
                      {p.onSale && p.originalPrice > p.price ? (
                        <span style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                          <s style={{ color: 'rgba(0,0,0,0.35)' }}>${p.originalPrice.toFixed(2)}</s>{' '}
                          <span style={{ color: '#A50B18' }}>${p.price.toFixed(2)}</span>
                          <span style={styles.saleBadge}> {p.saleLabel || 'SALE'}</span>
                        </span>
                      ) : (
                        p.price ? <span style={{ fontSize: '0.8rem', color: '#A50B18', fontFamily: 'monospace' }}>${p.price.toFixed(2)}</span> : null
                      )}
                    </div>
                    {p.description && (
                      <div style={styles.description}>{p.description}</div>
                    )}
                  </div>
                  <button style={styles.addToGuideBtn} onClick={() => addToGuide(p)}>
                    Add to Guide
                  </button>
                </div>
              ))}
              {filteredCatalog.length > 50 && (
                <div style={styles.modalEmpty}>Showing first 50 results. Refine your search to see more.</div>
              )}
            </div>
          </div>
        </div>
      )}

      <ProductImageModal product={zoomProduct} onClose={() => setZoomProduct(null)} />
    </div>
  );
}

/* ── Style helpers ── */

const th = (align = 'left') => ({
  padding: '0.75rem 1rem', textAlign: align,
  fontSize: '0.72rem', fontWeight: 600,
  color: 'rgba(0,0,0,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase',
  borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap',
});

const td = (align = 'left') => ({
  padding: '0.85rem 1rem', textAlign: align, verticalAlign: 'middle',
});

const styles = {
  root: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  loading: { color: 'rgba(0,0,0,0.4)', padding: '3rem', textAlign: 'center' },

  // Header
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' },
  title: { fontSize: '1.5rem', fontWeight: 700, color: '#333', margin: 0 },
  subtitle: { fontSize: '0.82rem', color: 'rgba(0,0,0,0.4)', margin: '0.3rem 0 0' },
  addFromCatalogBtn: {
    background: '#fff', color: '#A50B18', border: '1px solid #A50B18', borderRadius: 6,
    padding: '0.55rem 1.1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.15s',
  },

  // Banners
  successBanner: {
    background: 'rgba(39,174,96,0.08)', border: '1px solid rgba(39,174,96,0.25)', borderRadius: 8,
    padding: '1rem 1.25rem', color: '#27ae60', display: 'flex', alignItems: 'center', gap: '1rem',
  },
  viewBasketLink: { color: '#27ae60', fontWeight: 600, textDecoration: 'underline', fontSize: '0.9rem' },
  dismissBtn: { background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '1rem', marginLeft: 'auto' },
  errorBanner: { background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: 8, padding: '1rem 1.25rem', color: '#c0392b' },

  // True-empty state for customers with no order history yet. Shown
  // instead of the (otherwise blank) table.
  emptyGuide: {
    background: '#fff', border: '1px solid #ececec', borderRadius: 12,
    padding: '2.5rem 1.5rem', textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    maxWidth: 480, margin: '1rem auto',
  },
  emptyGuideIcon: { fontSize: '3rem', marginBottom: '0.75rem' },
  emptyGuideTitle: { fontSize: '1.25rem', fontWeight: 700, color: '#222', margin: '0 0 0.5rem' },
  emptyGuideText: { fontSize: '0.92rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.55, margin: '0 auto 1.5rem', maxWidth: 380 },
  emptyGuideBtn: {
    display: 'inline-block', background: '#A50B18', color: '#fff',
    borderRadius: 6, padding: '0.75rem 1.5rem',
    fontSize: '0.95rem', fontWeight: 700, textDecoration: 'none',
    letterSpacing: '0.02em',
  },
  emptyGuideSecondaryBtn: {
    display: 'block', margin: '0.85rem auto 0',
    background: 'none', border: 'none', color: '#A50B18',
    fontSize: '0.85rem', fontWeight: 500, cursor: 'pointer',
    textDecoration: 'underline', fontFamily: 'inherit',
  },

  // Search
  searchRow: { display: 'flex', gap: '1rem', alignItems: 'center' },
  description: { fontSize: '0.8rem', color: 'rgba(0,0,0,0.5)', lineHeight: 1.4, marginTop: '0.35rem', maxWidth: 560 },
  productRow: { display: 'flex', gap: '0.85rem', alignItems: 'flex-start' },
  productText: { flex: 1, minWidth: 0 },
  thumbnail: { width: 140, height: 140, objectFit: 'contain', flexShrink: 0, background: '#f7f5f2', borderRadius: 6, border: '1px solid #eee', cursor: 'zoom-in' },
  search: { flex: 1, background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '0.7rem 1rem', color: '#333', fontSize: '0.9rem', outline: 'none' },
  resultCount: { fontSize: '0.8rem', color: 'rgba(0,0,0,0.4)', whiteSpace: 'nowrap' },

  // Table
  tableWrap: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  row: { borderBottom: '1px solid #f0f0f0', transition: 'background 0.1s' },
  tdProduct: { padding: '0.85rem 1rem', minWidth: 460 },
  productName: { fontSize: '0.92rem', fontWeight: 500, color: '#333', marginBottom: 3 },
  productMeta: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' },
  sku: { fontSize: '0.72rem', color: 'rgba(0,0,0,0.35)', fontFamily: 'monospace' },
  brand: { fontSize: '0.72rem', color: '#A50B18', background: 'rgba(165,11,24,0.08)', padding: '0.1rem 0.4rem', borderRadius: 3 },
  orderBadge: { fontSize: '0.68rem', color: '#27ae60', background: 'rgba(39,174,96,0.08)', padding: '0.1rem 0.4rem', borderRadius: 3, fontWeight: 600 },
  customBadge: { fontSize: '0.68rem', color: '#2980b9', background: 'rgba(41,128,185,0.08)', padding: '0.1rem 0.4rem', borderRadius: 3, fontWeight: 600 },
  saleBadge: { fontSize: '0.68rem', color: '#fff', background: '#A50B18', padding: '0.1rem 0.4rem', borderRadius: 3, fontWeight: 700, letterSpacing: '0.03em' },
  specialsBanner: { display: 'block', background: 'rgba(165,11,24,0.06)', border: '1px solid rgba(165,11,24,0.2)', color: '#A50B18', borderRadius: 8, padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.88rem', fontWeight: 600, textDecoration: 'none' },

  // PAR input
  parInput: {
    width: 56, textAlign: 'center', background: '#fff', border: '1px solid #ddd', borderRadius: 4,
    color: '#999', fontSize: '0.85rem', padding: '0.3rem', fontFamily: 'monospace', outline: 'none',
  },

  // Qty control
  qtyControl: { display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' },
  qtyBtn: { width: 28, height: 28, background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 4, color: '#333', cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  qtyInput: { width: 56, textAlign: 'center', background: '#fff', border: '1px solid #ddd', borderRadius: 4, color: '#333', fontSize: '0.9rem', padding: '0.3rem', fontFamily: 'monospace', outline: 'none' },
  qtyInputParMismatch: { border: '1px solid #e67e22', background: 'rgba(230,126,34,0.05)' },
  parHint: { fontSize: '0.68rem', color: '#e67e22', marginTop: 2, textAlign: 'center', fontWeight: 600 },

  // Basket panel — sticks to the bottom of the viewport on desktop so it stays
  // in view the moment a customer adds an item, instead of hiding at the foot of
  // a long list. (Mobile turns this into a fixed footer; see index.html.)
  basketPanel: {
    position: 'sticky', bottom: '1.25rem', zIndex: 50,
    background: '#fff', border: '1px solid #A50B18', borderRadius: 12,
    padding: '0.9rem 1.5rem',
    boxShadow: '0 10px 30px rgba(165,11,24,0.20), 0 2px 8px rgba(0,0,0,0.10)',
  },
  basketSummary: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' },
  basketSummaryText: { display: 'flex', alignItems: 'center', gap: '0.55rem', fontSize: '1.05rem', fontWeight: 600, color: '#1a1a1a' },
  basketTotal: { fontWeight: 800, color: '#A50B18' },
  basketActions: { display: 'flex', gap: '0.85rem', alignItems: 'center' },
  clearBtn: { background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '0.55rem 0.9rem', fontSize: '0.82rem', color: 'rgba(0,0,0,0.45)', cursor: 'pointer' },
  addToBasketBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
    background: '#A50B18', color: '#fff', border: 'none', borderRadius: 8,
    padding: '0.8rem 1.75rem', fontSize: '1rem', fontWeight: 800, cursor: 'pointer',
    letterSpacing: '0.02em', boxShadow: '0 3px 10px rgba(165,11,24,0.35)',
  },

  // Modal
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#fff', borderRadius: 10, width: '90%', maxWidth: 640, maxHeight: '80vh',
    display: 'flex', flexDirection: 'column', boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
  },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid #e0e0e0' },
  modalTitle: { margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#333' },
  modalCloseBtn: { background: 'none', border: 'none', fontSize: '1.2rem', color: 'rgba(0,0,0,0.4)', cursor: 'pointer' },
  modalSearch: {
    margin: '1rem 1.5rem 0', padding: '0.7rem 1rem', border: '1px solid #ddd', borderRadius: 6,
    fontSize: '0.9rem', outline: 'none', color: '#333',
  },
  modalList: { flex: 1, overflowY: 'auto', padding: '0.75rem 1.5rem 1.5rem' },
  modalEmpty: { padding: '2rem', textAlign: 'center', color: 'rgba(0,0,0,0.3)', fontSize: '0.9rem' },
  modalItem: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem',
    padding: '0.75rem 0', borderBottom: '1px solid #f0f0f0',
  },
  modalItemInfo: { flex: 1, minWidth: 0 },
  modalItemName: { fontSize: '0.9rem', fontWeight: 500, color: '#333', marginBottom: 2 },
  modalItemMeta: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' },
  addToGuideBtn: {
    background: '#fff', color: '#A50B18', border: '1px solid #A50B18', borderRadius: 4,
    padding: '0.35rem 0.75rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
    whiteSpace: 'nowrap', flexShrink: 0,
  },
};
